/**
 * Variant chip selector — the row of clickable "12MM / 19MM" style
 * chips that appear at the top of every thickness- or weight-bulk
 * product card (Plywood, Blockboard, MDF, Doors, WPC/PVC, Adhesives).
 *
 * Extracted from ProductsPage.js. Handles both:
 *   • THICKNESS bulk (olive green chips, sorted numerically)
 *   • WEIGHT bulk (amber chips, sorted by unit type then numerically)
 *
 * One shared component instead of two near-identical blocks — keeps
 * the sorting rules for adhesive weights (GMS < LTR < KG) in a single
 * place so a future tweak can't diverge between the two card types.
 */
import React from 'react';
import { formatINR } from '../../../lib/store';


function sortByWeightThenNumber(variants) {
  const getUnit = (w) => {
    if (!w) return 'OTHER';
    const upper = w.toUpperCase();
    if (upper.includes('KG')) return 'KG';
    if (upper.includes('GMS') || upper.includes('GM')) return 'GMS';
    if (upper.includes('LTR') || upper.includes('LITRE')) return 'LTR';
    return 'OTHER';
  };
  const unitOrder = { 'GMS': 1, 'LTR': 2, 'KG': 3, 'OTHER': 4 };
  return variants.slice().sort((a, b) => {
    const aUnit = getUnit(a.thickness);
    const bUnit = getUnit(b.thickness);
    if (aUnit !== bUnit) return (unitOrder[aUnit] || 5) - (unitOrder[bUnit] || 5);
    const aVal = parseFloat((a.thickness || '0').replace(/[^\d.]/g, ''));
    const bVal = parseFloat((b.thickness || '0').replace(/[^\d.]/g, ''));
    return aVal - bVal;
  });
}


function sortByNumericThickness(variants) {
  return variants.slice().sort((a, b) => {
    const aVal = parseFloat((a.thickness || '0').replace(/[^\d.]/g, ''));
    const bVal = parseFloat((b.thickness || '0').replace(/[^\d.]/g, ''));
    return aVal - bVal;
  });
}


export function VariantChipSelector({ group, mode, onSelect }) {
  const isWeight = mode === 'weight';
  const sorted = isWeight ? sortByWeightThenNumber(group.variants) : sortByNumericThickness(group.variants);
  const chipClass = 'border border-heda-line bg-white text-heda-ink hover:border-heda-gold hover:bg-heda-gold-tint';

  return (
    <div className="mb-3">
      <p className="text-xs text-heda-mute uppercase tracking-wide mb-2">
        {isWeight ? (
          <>
            Available Weights <span className="text-amber-700">(Adhesives)</span>
          </>
        ) : (
          <>
            Available Thicknesses{' '}
            {group.category !== 'Plywood' && (
              <span className="text-heda-gold">({group.category})</span>
            )}
          </>
        )}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {sorted.map((variant, vIdx) => {
          // Show the rate under the thickness/weight so staff
          // don't have to hover for the tooltip. Rate line is suppressed
          // when base_rate is missing / 0 so the chip stays unchanged
          // for un-priced variants; we never print "₹0".
          const baseRate = Number(variant.base_rate || 0);
          const showRate = baseRate > 0;
          const suffix = !isWeight && variant.rate_type
            ? `/${variant.rate_type.replace(/^per\s+/i, '')}`
            : '';
          return (
            <button
              key={vIdx}
              onClick={() => onSelect(group, variant.thickness)}
              className={`px-3 py-1 leading-tight text-xs font-semibold rounded-xl transition-all cursor-pointer flex flex-col items-center ${chipClass}`}
              title={
                isWeight
                  ? formatINR(variant.base_rate)
                  : `${formatINR(variant.base_rate)}/${variant.rate_type?.replace('per ', '')}`
              }
              data-testid={`variant-chip-${(variant.thickness || '').toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
            >
              <span>{variant.thickness}</span>
              {showRate && (
                <span className="text-[10px] text-heda-mute mt-0.5" data-testid="variant-chip-rate">
                  {formatINR(baseRate)}{suffix}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
